// ==============================================
// MyPolaris — Polaris Track (public vessel tracking) settings page.
//
// Per Scout: visibility (Off / Unlisted link / Public listing), the share
// link, MMSI + display name for routers, the course-suggestion opt-in, the
// passage log that replaces the daily position email, and the queue of
// router course suggestions to accept (into the Navigation Library, and
// optionally straight to the boat) or decline.
//
// Package-gated by packages.public_tracking — the sidebar entry is hidden
// for unentitled accounts; this page keeps its own gate for direct URLs.
// ==============================================

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  SignalIcon,
  ClipboardDocumentIcon,
  ArrowTopRightOnSquareIcon,
  CheckIcon,
  XMarkIcon,
  MapPinIcon,
  ArrowPathIcon,
  PaperAirplaneIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { api } from '../../services/api';
import { useToastStore } from '../../store/toastStore';
import type { TrackingDevice, TrackingVisibility, CourseSuggestion } from '../../types/tracking';

const VISIBILITY: { value: TrackingVisibility; label: string; blurb: string }[] = [
  { value: 'off', label: 'Off', blurb: 'Nothing is published. Your position stays private to your account.' },
  { value: 'unlisted', label: 'Unlisted', blurb: 'Only people with your link can follow. Not searchable, not on the fleet map.' },
  { value: 'public', label: 'Public', blurb: 'Listed on track.vortexnav.com and findable by name, MMSI or your MyPolaris email.' },
];

const fmtPos = (lat: number, lon: number) => {
  const dm = (v: number, pos: string, neg: string) => {
    const a = Math.abs(v);
    const d = Math.floor(a);
    return `${d}°${((a - d) * 60).toFixed(2).padStart(5, '0')}′${v >= 0 ? pos : neg}`;
  };
  return `${dm(lat, 'N', 'S')}  ${dm(lon, 'E', 'W')}`;
};

const fmtWhen = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString('en-NZ', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const isOnline = (iso: string | null) => iso != null && Date.now() - new Date(iso).getTime() < 10 * 60_000;

/** Most recently seen first, so the Scout in use sits at the top of a long list. */
export const sortByLastSeen = <T extends { last_seen_at: string | null }>(devices: T[]): T[] =>
  [...devices].sort((a, b) => (b.last_seen_at ? Date.parse(b.last_seen_at) : 0) - (a.last_seen_at ? Date.parse(a.last_seen_at) : 0));

export function TrackingPage() {
  const queryClient = useQueryClient();

  const { data: access, isLoading: accessLoading } = useQuery({ queryKey: ['tracking-access'], queryFn: () => api.trackingAccess() });
  const { data: devices, isLoading } = useQuery({
    queryKey: ['tracking-devices'],
    queryFn: () => api.trackingDevices(),
    enabled: access?.enabled === true,
    refetchInterval: 60_000,
  });
  const { data: suggestions } = useQuery({
    queryKey: ['tracking-suggestions'],
    queryFn: () => api.trackingSuggestions(),
    enabled: access?.enabled === true,
    refetchInterval: 60_000,
  });

  if (accessLoading) return <div className="p-8 text-slate-400 text-base">Loading…</div>;

  if (access?.enabled === false) {
    return (
      <div className="max-w-2xl mx-auto mt-16 rounded-2xl border border-slate-700 bg-slate-800/60 p-8 text-center">
        <SignalIcon className="w-12 h-12 mx-auto text-cyan-400" />
        <h1 className="mt-4 text-2xl font-bold text-white">Polaris Track isn’t in your plan</h1>
        <p className="mt-3 text-base text-slate-300">
          Polaris Track publishes your vessel’s live position and instruments to a page your family, weather router or
          rescue coordinator can follow — with a passage log and course suggestions that flow straight into your Navigation Library.
        </p>
        <Link to="/dashboard/billing" className="inline-block mt-6 px-5 py-2.5 rounded-lg bg-cyan-500 text-slate-900 font-semibold text-base hover:bg-cyan-400">
          See plans
        </Link>
      </div>
    );
  }

  const pending = (suggestions ?? []).filter((s) => s.status === 'pending');
  const decided = (suggestions ?? []).filter((s) => s.status !== 'pending').slice(0, 10);

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-8">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <SignalIcon className="w-8 h-8 text-cyan-400" /> Polaris Track
          </h1>
          <p className="mt-2 text-base text-slate-300 max-w-2xl">
            Share where you are. Choose who can see each Scout, keep a passage log for the people following you, and
            review course suggestions from your weather router.
          </p>
        </div>
        <a href="https://track.vortexnav.com" target="_blank" rel="noreferrer"
           className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-600 text-base text-slate-200 hover:bg-slate-800">
          Open Polaris Track <ArrowTopRightOnSquareIcon className="w-4 h-4" />
        </a>
      </header>

      {pending.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-xl font-semibold text-white">Course suggestions waiting for you</h2>
          {pending.map((s) => (
            <SuggestionCard key={s.id} s={s} devices={devices ?? []}
              onDone={() => { queryClient.invalidateQueries({ queryKey: ['tracking-suggestions'] }); queryClient.invalidateQueries({ queryKey: ['tracking-devices'] }); queryClient.invalidateQueries({ queryKey: ['nav-list'] }); }} />
          ))}
        </section>
      )}

      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-white">Your Scouts</h2>
        {isLoading && <div className="text-slate-400 text-base">Loading your Scouts…</div>}
        {!isLoading && (devices ?? []).length === 0 && (
          <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-6 text-base text-slate-300">
            No Scouts are registered to this account yet. Register one under <Link to="/dashboard/devices" className="text-cyan-400 underline">Devices</Link> and it will appear here.
          </div>
        )}
        {sortByLastSeen(devices ?? []).map((d) => (
          <DeviceCard key={d.device_id} d={d} onChanged={() => queryClient.invalidateQueries({ queryKey: ['tracking-devices'] })} />
        ))}
      </section>

      {decided.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-lg font-semibold text-slate-200">Recently decided</h2>
          <ul className="divide-y divide-slate-800 rounded-xl border border-slate-800 bg-slate-900/60">
            {decided.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-3 px-4 py-3 text-base">
                <span className="text-slate-300 truncate">
                  <span className="font-medium text-slate-100">{s.submitterName}</span> · {s.waypoints.length} waypoints · {fmtWhen(s.createdAt)}
                </span>
                <span className={clsx('shrink-0 px-2.5 py-0.5 rounded-full text-sm font-semibold',
                  s.status === 'accepted' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-700 text-slate-300')}>
                  {s.status === 'accepted' ? 'Accepted' : 'Declined'}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function DeviceCard({ d, onChanged }: { d: TrackingDevice; onChanged: () => void }) {
  const addToast = useToastStore((s) => s.addToast);
  const [mmsi, setMmsi] = useState(d.mmsi ?? '');
  const [vesselName, setVesselName] = useState(d.vessel_name ?? '');
  const [routeLabel, setRouteLabel] = useState(d.route_label ?? '');
  const [showLog, setShowLog] = useState(false);
  const [confirmPassage, setConfirmPassage] = useState(false);
  const [open, setOpen] = useState(false); // collapsed by default — long fleets scroll a long way otherwise

  const update = useMutation({
    mutationFn: (patch: Parameters<typeof api.trackingUpdate>[1]) => api.trackingUpdate(d.device_id, patch),
    onSuccess: () => onChanged(),
    onError: (e: unknown) => addToast({ type: 'error', title: 'Not saved', message: errMsg(e) }),
  });
  const newPassage = useMutation({
    mutationFn: () => api.trackingNewPassage(d.device_id),
    onSuccess: () => { setConfirmPassage(false); onChanged(); addToast({ type: 'success', title: 'New passage started', message: 'Followers now see the track from this moment on.' }); },
    onError: (e: unknown) => addToast({ type: 'error', title: 'Could not start a new passage', message: errMsg(e) }),
  });

  const detailsDirty = (mmsi || '') !== (d.mmsi ?? '') || (vesselName || '') !== (d.vessel_name ?? '') || (routeLabel || '') !== (d.route_label ?? '');
  const online = isOnline(d.last_seen_at);
  const on = d.visibility !== 'off';

  const copyLink = async () => {
    if (!d.share_url) return;
    try {
      await navigator.clipboard.writeText(d.share_url);
      addToast({ type: 'success', title: 'Link copied', message: d.share_url });
    } catch {
      addToast({ type: 'info', title: 'Copy this link', message: d.share_url });
    }
  };

  return (
    <div className={clsx('rounded-2xl border p-5', open && 'space-y-5', on ? 'border-cyan-700/60 bg-slate-800/70' : 'border-slate-700 bg-slate-800/40')}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
        className="w-full flex flex-wrap items-start justify-between gap-3 text-left rounded-lg -m-1 p-1 hover:bg-slate-700/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={clsx('w-2.5 h-2.5 rounded-full', online ? 'bg-emerald-400' : 'bg-slate-500')} />
            <h3 className="text-xl font-semibold text-white truncate">{d.vessel_name || d.device_name || 'Scout'}</h3>
            {d.vessel_name && d.device_name && <span className="text-base text-slate-400 truncate">· {d.device_name}</span>}
            <span className={clsx('ml-1 px-2 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide',
              on ? 'bg-cyan-500/15 text-cyan-300' : 'bg-slate-700 text-slate-400')}>
              {VISIBILITY.find((v) => v.value === d.visibility)?.label ?? d.visibility}
            </span>
          </div>
          <p className="mt-1 text-base text-slate-400">
            {online ? 'Online' : `Last seen ${fmtWhen(d.last_seen_at)}`}
            {d.last_position && <span className="ml-3 font-mono text-slate-300">{fmtPos(d.last_position.lat, d.last_position.lon)}</span>}
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {d.pending_suggestions > 0 && (
            <span className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-300 text-sm font-semibold">
              {d.pending_suggestions} suggestion{d.pending_suggestions === 1 ? '' : 's'} waiting
            </span>
          )}
          <ChevronDownIcon className={clsx('w-5 h-5 text-slate-400 transition-transform', open && 'rotate-180')} />
        </div>
      </button>

      {open && <>
      {/* Visibility */}
      <div>
        <div className="text-sm font-semibold uppercase tracking-wide text-slate-400 mb-2">Who can see this Scout</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {VISIBILITY.map((v) => (
            <button key={v.value} type="button" disabled={update.isPending}
              onClick={() => v.value !== d.visibility && update.mutate({ visibility: v.value })}
              className={clsx('text-left rounded-xl border px-4 py-3 transition',
                d.visibility === v.value ? 'border-cyan-400 bg-cyan-500/15 text-white' : 'border-slate-600 text-slate-300 hover:border-slate-400')}>
              <div className="text-base font-semibold">{v.label}</div>
              <div className="text-sm text-slate-400 mt-0.5 leading-snug">{v.blurb}</div>
            </button>
          ))}
        </div>
      </div>

      {on && d.share_url && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl bg-slate-900/70 border border-slate-700 px-4 py-3">
          <span className="text-sm font-semibold uppercase tracking-wide text-slate-400 mr-1">Share link</span>
          <code className="text-base text-cyan-300 truncate flex-1 min-w-[12rem]">{d.share_url}</code>
          <button type="button" onClick={copyLink} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-base text-white">
            <ClipboardDocumentIcon className="w-4 h-4" /> Copy
          </button>
          <a href={d.share_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-base text-white">
            <ArrowTopRightOnSquareIcon className="w-4 h-4" /> Open
          </a>
        </div>
      )}

      {/* Vessel details for routers */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1.4fr_auto] gap-3 items-end">
        <label className="block">
          <span className="text-sm font-semibold uppercase tracking-wide text-slate-400">Vessel name shown</span>
          <input value={vesselName} onChange={(e) => setVesselName(e.target.value)} placeholder={d.device_name ?? 'Vessel name'} maxLength={80}
            className="mt-1 w-full rounded-lg bg-slate-900 border border-slate-600 px-3 py-2 text-base text-white placeholder-slate-500 focus:border-cyan-400 focus:outline-none" />
        </label>
        <label className="block">
          <span className="text-sm font-semibold uppercase tracking-wide text-slate-400">MMSI</span>
          <input value={mmsi} onChange={(e) => setMmsi(e.target.value.replace(/\D/g, '').slice(0, 9))} placeholder="9 digits" inputMode="numeric"
            className="mt-1 w-full rounded-lg bg-slate-900 border border-slate-600 px-3 py-2 text-base font-mono text-white placeholder-slate-500 focus:border-cyan-400 focus:outline-none" />
        </label>
        <label className="block">
          <span className="text-sm font-semibold uppercase tracking-wide text-slate-400">Route shown</span>
          <input value={routeLabel} onChange={(e) => setRouteLabel(e.target.value)} placeholder="e.g. Ōpua → Lautoka" maxLength={120}
            className="mt-1 w-full rounded-lg bg-slate-900 border border-slate-600 px-3 py-2 text-base text-white placeholder-slate-500 focus:border-cyan-400 focus:outline-none" />
        </label>
        <button type="button" disabled={!detailsDirty || update.isPending}
          onClick={() => update.mutate({ mmsi: mmsi || null, vessel_name: vesselName || null, route_label: routeLabel || null }, { onSuccess: () => addToast({ type: 'success', title: 'Vessel details saved' }) })}
          className="px-4 py-2 rounded-lg bg-cyan-500 text-slate-900 font-semibold text-base hover:bg-cyan-400 disabled:opacity-40 disabled:cursor-not-allowed">
          Save
        </button>
      </div>

      {/* Suggestions opt-in */}
      <label className="flex items-start gap-3 cursor-pointer">
        <input type="checkbox" checked={d.allow_suggestions} disabled={update.isPending}
          onChange={(e) => update.mutate({ allow_suggestions: e.target.checked })}
          className="mt-1 w-5 h-5 rounded bg-slate-700 border-slate-500 text-cyan-500 focus:ring-cyan-500" />
        <span>
          <span className="text-base font-medium text-slate-100">Accept course suggestions from viewers</span>
          <span className="block text-sm text-slate-400">A weather router can plot a course on your public page. Nothing changes on your Scout until you accept it here.</span>
        </span>
      </label>

      {/* Passage controls */}
      <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-slate-700/70">
        <span className="text-sm text-slate-400 mr-auto">
          {d.passage_started_at ? `Passage started ${fmtWhen(d.passage_started_at)}` : 'No passage started yet'}
        </span>
        <button type="button" onClick={() => setShowLog((v) => !v)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-600 text-base text-slate-200 hover:bg-slate-700">
          <MapPinIcon className="w-4 h-4" /> Passage log
        </button>
        <button type="button" onClick={() => setConfirmPassage(true)} disabled={!on}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-600 text-base text-slate-200 hover:bg-slate-700 disabled:opacity-40">
          <ArrowPathIcon className="w-4 h-4" /> Start new passage
        </button>
      </div>

      {showLog && <PassageLog deviceId={d.device_id} />}
      </>}

      {confirmPassage && (
        <Modal title="Start a new passage?" onClose={() => setConfirmPassage(false)}>
          <p className="text-base text-slate-300">
            Followers will see your track from now on; the previous passage’s track and log entries leave the public page. Your data is kept.
          </p>
          <div className="mt-5 flex justify-end gap-2">
            <button type="button" onClick={() => setConfirmPassage(false)} className="px-4 py-2 rounded-lg border border-slate-600 text-base text-slate-200 hover:bg-slate-700">Cancel</button>
            <button type="button" onClick={() => newPassage.mutate()} disabled={newPassage.isPending} className="px-4 py-2 rounded-lg bg-cyan-500 text-slate-900 font-semibold text-base hover:bg-cyan-400">Start new passage</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function PassageLog({ deviceId }: { deviceId: string }) {
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);
  const [body, setBody] = useState('');
  const { data: entries, isLoading } = useQuery({ queryKey: ['tracking-log', deviceId], queryFn: () => api.trackingLog(deviceId) });
  const add = useMutation({
    mutationFn: () => api.trackingAddLog(deviceId, body),
    onSuccess: () => { setBody(''); queryClient.invalidateQueries({ queryKey: ['tracking-log', deviceId] }); addToast({ type: 'success', title: 'Log entry posted' }); },
    onError: (e: unknown) => addToast({ type: 'error', title: 'Not posted', message: errMsg(e) }),
  });

  return (
    <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-4 space-y-3">
      <div className="text-sm font-semibold uppercase tracking-wide text-slate-400">Passage log — shown on your public page</div>
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} maxLength={2000}
        placeholder="e.g. 0600 All well aboard. Wind 18 kn from the SE, making 6.5 kn under full main and jib. ETA Savusavu Friday."
        className="w-full rounded-lg bg-slate-900 border border-slate-600 px-3 py-2 text-base text-white placeholder-slate-500 focus:border-cyan-400 focus:outline-none" />
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-500">{body.length}/2000</span>
        <button type="button" disabled={!body.trim() || add.isPending} onClick={() => add.mutate()}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-500 text-slate-900 font-semibold text-base hover:bg-cyan-400 disabled:opacity-40">
          <PaperAirplaneIcon className="w-4 h-4" /> Post entry
        </button>
      </div>
      {isLoading && <div className="text-base text-slate-400">Loading…</div>}
      <ul className="space-y-2">
        {(entries ?? []).map((e) => (
          <li key={e.id} className="rounded-lg bg-slate-800/80 px-3 py-2">
            <div className="text-sm text-slate-400">{fmtWhen(e.createdAt)}</div>
            <div className="text-base text-slate-100 whitespace-pre-wrap">{e.body}</div>
          </li>
        ))}
        {!isLoading && (entries ?? []).length === 0 && <li className="text-base text-slate-500">No entries yet.</li>}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------

function SuggestionCard({ s, devices, onDone }: { s: CourseSuggestion; devices: TrackingDevice[]; onDone: () => void }) {
  const addToast = useToastStore((st) => st.addToast);
  const [sendTo, setSendTo] = useState<string | null>(s.deviceId);
  const accept = useMutation({
    mutationFn: () => api.trackingAcceptSuggestion(s.id, sendTo),
    onSuccess: (r) => {
      onDone();
      addToast({
        type: 'success',
        title: 'Course accepted into your Navigation Library',
        message: r.sent_to ? (r.online ? `Sent to ${r.sent_to}` : `Queued for ${r.sent_to} — arrives when it is next online`) : undefined,
      });
    },
    onError: (e: unknown) => addToast({ type: 'error', title: 'Could not accept', message: errMsg(e) }),
  });
  const decline = useMutation({
    mutationFn: () => api.trackingDeclineSuggestion(s.id),
    onSuccess: () => { onDone(); addToast({ type: 'info', title: 'Suggestion declined' }); },
    onError: (e: unknown) => addToast({ type: 'error', title: 'Could not decline', message: errMsg(e) }),
  });

  return (
    <div className="rounded-2xl border border-amber-500/50 bg-amber-500/5 p-5 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-lg font-semibold text-white">
            Suggested course from <span className="text-amber-300">{s.submitterName}</span>
          </div>
          <div className="text-base text-slate-400">
            {fmtWhen(s.createdAt)} · for {s.device_name ?? 'your Scout'} · {s.waypoints.length} waypoints
            {s.submitterEmail && <> · <a className="underline" href={`mailto:${s.submitterEmail}`}>{s.submitterEmail}</a></>}
          </div>
        </div>
      </div>
      {s.message && <p className="text-base text-slate-100 whitespace-pre-wrap rounded-lg bg-slate-900/60 px-3 py-2">“{s.message}”</p>}
      <ol className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-base font-mono text-slate-200">
        {s.waypoints.map((w, i) => (
          <li key={i} className="flex gap-2 items-baseline">
            <span className="text-slate-500 w-6 text-right">{i + 1}.</span>
            <span className="text-slate-400 w-16 truncate">{w.name}</span>
            <span>{fmtPos(w.lat, w.lon)}</span>
            {w.eta && <span className="text-slate-500 text-sm">arr {fmtWhen(w.eta)}</span>}
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-amber-500/20">
        <label className="flex items-center gap-2 text-base text-slate-200 mr-auto">
          <input type="checkbox" checked={sendTo != null} onChange={(e) => setSendTo(e.target.checked ? s.deviceId : null)}
            className="w-5 h-5 rounded bg-slate-700 border-slate-500 text-cyan-500 focus:ring-cyan-500" />
          Also send to {devices.find((d) => d.device_id === s.deviceId)?.device_name ?? 'my Scout'}
        </label>
        <button type="button" onClick={() => decline.mutate()} disabled={decline.isPending || accept.isPending}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-600 text-base text-slate-200 hover:bg-slate-700">
          <XMarkIcon className="w-4 h-4" /> Decline
        </button>
        <button type="button" onClick={() => accept.mutate()} disabled={accept.isPending || decline.isPending}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500 text-slate-900 font-semibold text-base hover:bg-emerald-400">
          <CheckIcon className="w-4 h-4" /> Accept into library
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-xl font-semibold text-white">{title}</h3>
        <div className="mt-3">{children}</div>
      </div>
    </div>
  );
}

function errMsg(e: unknown): string {
  const r = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
  return r ?? (e instanceof Error ? e.message : 'Something went wrong');
}
